import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import getConditionalData from '../utils/query/getConditionalData';
import getConditionalDataFlag from '../utils/query/getConditionalDataFlag';
import normalizeQueryResults from '../utils/query/normalizeQueryResults';
import ShareclassCardLabelValue from './ShareclassCardLabelValue';
import Icon from './Icon';
import { computed } from 'mobx';
import getQueryResults from '../utils/query/getQueryResults';
import Tooltip from './Tooltip';
import { camelCase } from 'change-case';
import {
    AccessibilityStore,
    DataStore,
    EntityGroupStore,
    EntityStore,
} from '../stores';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { ShareClass } from '../models/entity';
import { Theme } from '../models/Theme';

const ShareclassCardWrapper = styled<any, any>(
    'div',
    'shareclassCardWrapper',
    'shareclassCards'
)`
    ${propStyles};
`;

ShareclassCardWrapper.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    margin: 'md',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    boxShadow: 'sm',
};

const ShareclassCardExpandLabel = styled<any, any>(
    'span',
    'shareclassCardExpandLabel',
    'shareclassCards'
)`
    ${propStyles};
`;

ShareclassCardExpandLabel.ShareclassCardLabel = {
    fontFamily: 'heading',
    fontWeight: 'medium',
};

export const ShareclassCardList = styled<any, any>(
    'ul',
    'shareclassCardList',
    'shareclassCards'
)`
    ${propStyles};
`;

ShareclassCardList.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    paddingX: 'md',
};

export const ShareclassCardRow = styled<any, any>(
    'li',
    'shareclassCardRow',
    'shareclassCards'
)`
    ${propStyles};
    position: relative;
    list-style: none;
`;

ShareclassCardRow.defaultProps = {
    display: 'flex',
    flex: '1 0 auto',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    paddingY: 'md',
    fontSize: 'sm',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
};

const ExpandIcon = styled<any, any>(Icon, 'expandIcon', 'shareclassCards')`
    transition: transform 300ms ease-in-out;
    &.open {
        transform: rotate3d(1, 0, 0, 180deg);
    }
`;

ExpandIcon.defaultProps = {
    color: 'primary',
};

const ShareclassCardExpandButton = styled<any, any>(
    'button',
    'shareclassCardExpandButton',
    'shareclassCards'
)`
    ${propStyles};
    outline: none;
    cursor: pointer;
`;

ShareclassCardExpandButton.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginX: 'md',
    marginY: 'md',
    borderStyle: 'none',
    borderWidth: '0',
    bgColor: 'transparent',
};

interface ShareclassCardsProps {
    theme?: Theme;
    /** Injected by withShareclass */
    entityStore?: EntityStore;
    dataStore?: DataStore;
    entityGroupStore?: EntityGroupStore;
    /** */
    accessibilityStore?: AccessibilityStore;
    columns: Array<ColumnConfig>;
    alternated: boolean;
    shareClass?: ShareClass;
    rowAccessibleContext: string;
}

@inject('accessibilityStore', 'dataStore', 'entityStore')
@observer
class ShareclassCards extends Component<ShareclassCardsProps, any> {
    @computed
    get checkEnableByFundList() {
        const { dataStore, entityStore, entityGroupStore } = this.props;
        const fundListCheck = entityGroupStore.rowExpanderConfig?.fundListCheck;
        if (fundListCheck) {
            return dataStore.fundListContainsEntity(
                entityStore.value as any,
                fundListCheck
            );
        }
        return true;
    }

    @computed
    get isRowExpandDisabled() {
        if (
            !this.checkEnableByFundList ||
            this.props.entityStore.isShareclassDisabled
        ) {
            return true;
        }
        return false;
    }

    @computed
    get disabledTooltipDisclaimer() {
        const { dataStore, entityStore, entityGroupStore } = this.props;
        if (entityGroupStore.rowExpanderConfig.disabledTooltipDisclaimer) {
            const disclaimerQuery = {
                disclaimer:
                    entityGroupStore.rowExpanderConfig
                        .disabledTooltipDisclaimer,
            };
            const data = getQueryResults(
                entityStore.queryContext,
                disclaimerQuery,
                dataStore.culture
            );
            if (data && data.disclaimer) {
                return data.disclaimer;
            }
        }
        return;
    }

    @computed
    get cardExpander() {
        const {
            theme,
            entityStore,
            entityGroupStore,
            accessibilityStore,
            rowAccessibleContext,
        } = this.props;
        const { rowExpanderConfig } = entityGroupStore;
        const { value, toggleExpanded } = entityStore;

        let rowExpandable = !entityStore.disableRow;
        if (rowExpanderConfig && rowExpanderConfig.disabledFlag) {
            const conditionalConfig = rowExpanderConfig.disabledFlag;
            if (typeof conditionalConfig === 'boolean') {
                rowExpandable = !conditionalConfig;
            } else {
                const values = normalizeQueryResults({
                    ...value.properties_pub,
                    ...getConditionalData(value, conditionalConfig),
                });
                rowExpandable = !getConditionalDataFlag(
                    values,
                    conditionalConfig
                );
            }
        }

        if (rowExpandable && !this.isRowExpandDisabled) {
            const iconFileName = entityStore.expanded
                ? (rowExpanderConfig &&
                    rowExpanderConfig.mobileCollapseIconFileName) ||
                'MobileCollapseIcon'
                : (rowExpanderConfig &&
                    rowExpanderConfig.mobileExpandIconFileName) ||
                'MobileExpandIcon';

            return (
                <ShareclassCardExpandButton
                    aria-label={accessibilityStore.buttonAccessibilityValue(
                        'ShareclassCardExpandButton'
                    )}
                    onClick={toggleExpanded}
                    data-metric-id={`shareclassCard-expand`}
                    {...theme.ShareclassCardExpandButton}
                >
                    {rowExpanderConfig &&
                        rowExpanderConfig.displayType &&
                        rowExpanderConfig.displayType.map(item => {
                            if (item === 'text' && rowExpanderConfig.label) {
                                return (
                                    <ShareclassCardExpandLabel
                                        {...theme.ShareclassCardExpandLabel}
                                    >
                                        {rowExpanderConfig.label}
                                    </ShareclassCardExpandLabel>
                                );
                            }

                            if (item === 'icon') {
                                return (
                                    <Icon
                                        iconSize="1.4em"
                                        fileName={iconFileName}
                                        {...theme.ShareclassCardExpandIcon}
                                        accessibilityContext={`for share class identified by ${value.clientCode}`}
                                    />
                                );
                            }
                        })}
                </ShareclassCardExpandButton>
            );
        }
        if (this.isRowExpandDisabled) {
            const disabledTooltip = this.disabledTooltipDisclaimer;
            if (disabledTooltip) {
                return (
                    <>
                        <ShareclassCardExpandButton
                            aria-label={accessibilityStore.buttonAccessibilityValue(
                                'ShareclassCardExpandButton'
                            )}
                            {...theme.ShareclassCardExpandButtonDisabled}
                        />
                        <Tooltip
                            content={[disabledTooltip]}
                            accessibilityContext={{
                                key: `More information about the disabled card`,
                            }}
                            rowAccessibleContext={rowAccessibleContext}
                            analyticsContext={{
                                metricContext: 'shareclassCard-disabled',
                                source: 'ShareclassCardExpandButton',
                                // Disabled tooltip disclaimer is queried, so no disclaimer type is available to set, defaulting to: disabled_tooltip
                                tooltipCode: 'disabled_tooltip',
                            }}
                        />
                    </>
                );
            }
        }
    }

    render() {
        const {
            accessibilityStore,
            theme,
            alternated,
            entityStore,
            entityGroupStore,
            children,
            dataStore,
            rowAccessibleContext,
            ...shareClassProp
        } = this.props;

        const { rowExpanderConfig } = entityGroupStore;

        const rowExpandable = !this.isRowExpandDisabled;
        const shareclassCardWrapperTheme = rowExpandable
            ? theme.ShareclassCardWrapper
            : {
                ...theme.ShareclassCardWrapper,
                ...theme.ShareclassCardWrapperDisabled,
            };

        const shareclassCardListTheme = rowExpandable
            ? theme.ShareclassCardList
            : {
                ...theme.ShareclassCardList,
                ...theme.ShareclassCardListDisabled,
            };

        const shareclassCardRowHasSubListTheme = rowExpandable
            ? theme.ShareclassCardRowHasSubList
            : {
                ...theme.ShareclassCardRowHasSubList,
                ...theme.ShareclassCardRowHasSubListDisabled,
            };

        const shareclassCardRowTheme = rowExpandable
            ? theme.ShareclassCardRow
            : {
                ...theme.ShareclassCardRow,
                ...theme.ShareclassCardRowDisabled,
            };

        const contentPosition =
            (rowExpanderConfig && rowExpanderConfig.contentPosition) ||
            'default';
        return (
            <ShareclassCardWrapper
                data-alternated={alternated}
                {...shareclassCardWrapperTheme}
            >
                <ShareclassCardList {...shareclassCardListTheme}>
                    {shareClassProp.columns.map((column, index) => {
                        const rowTheme =
                            ['group', 'dropdown'].includes(column.type) &&
                                shareclassCardRowHasSubListTheme
                                ? shareclassCardRowHasSubListTheme
                                : shareclassCardRowTheme;
                        return (
                            <Fragment key={`${column.label}-${index}`}>
                                <ShareclassCardRow
                                    onClick={
                                        column.type === 'download'
                                            ? entityStore.toggleExpanded
                                            : () => null
                                    }
                                    cursor={
                                        column.type === 'download'
                                            ? 'pointer'
                                            : 'auto'
                                    }
                                    data-metric-id={`shareclassCard-row-${column.label
                                        ? camelCase(column.label)
                                        : camelCase(column.type)
                                        }`}
                                    {...rowTheme}
                                >
                                    <ShareclassCardLabelValue
                                        shareClass={shareClassProp}
                                        column={column}
                                        columnIndex={index}
                                        rowAccessibleContext={
                                            rowAccessibleContext
                                        }
                                    />
                                </ShareclassCardRow>
                                {entityStore.expanded &&
                                    rowExpandable &&
                                    contentPosition === 'inline' &&
                                    column.type === 'download' &&
                                    children}
                            </Fragment>
                        );
                    })}
                </ShareclassCardList>
                {entityStore.expanded &&
                    rowExpandable &&
                    contentPosition !== 'inline' &&
                    children}
                {this.cardExpander}
            </ShareclassCardWrapper>
        );
    }
}

export default withTheme(ShareclassCards) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassCards.tsx